import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle2,
  XCircle,
  Sparkles,
  ReceiptText,
  ChevronLeft,
} from "lucide-react";

import BrandLogo from "@/shared/components/BrandLogo";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/shared/ui/card";
import { cn } from "@/shared/lib/utils";

type BillingOutcomeLayoutProps = {
  status: "success" | "cancel";
  eyebrow: string;
  title: string;
  description: ReactNode;
  detail: string;
  ctaLabel: string;
  ctaTo: string;
  secondaryLabel?: string;
  secondaryTo?: string;
  planName?: string;
};

const statusConfig = {
  success: {
    themeColor: "emerald",
    badgeClassName:
      "border-emerald-500/20 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
    iconWrapClassName:
      "bg-emerald-500 text-white shadow-xl shadow-emerald-500/20",
    panelGlow: "bg-emerald-500/5",
    statusTextClassName: "text-emerald-600 dark:text-emerald-400",
    Icon: CheckCircle2,
    Decoration: Sparkles,
  },
  cancel: {
    themeColor: "amber",
    badgeClassName:
      "border-amber-500/20 bg-amber-500/10 text-amber-700 dark:text-amber-400",
    iconWrapClassName: "bg-amber-500 text-white shadow-xl shadow-amber-500/20",
    panelGlow: "bg-amber-500/5",
    statusTextClassName: "text-amber-600 dark:text-amber-400",
    Icon: XCircle,
    Decoration: ReceiptText,
  },
} as const;

export default function BillingOutcomeLayout({
  status,
  eyebrow,
  title,
  description,
  detail,
  ctaLabel,
  ctaTo,
  secondaryLabel,
  secondaryTo,
  planName,
}: BillingOutcomeLayoutProps) {
  const config = statusConfig[status];
  const { Icon, Decoration } = config;

  return (
    <main className="relative min-h-screen overflow-hidden bg-background selection:bg-primary/10">
      {/* Background Elements */}
      <div className="absolute inset-0 -z-10 overflow-hidden">
        <div
          className={cn(
            "absolute -top-[10%] left-1/2 -translate-x-1/2 w-full h-[40%] opacity-20 blur-[120px] rounded-full transition-colors duration-1000",
            status === "success" ? "bg-emerald-500" : "bg-amber-500",
          )}
        />
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-[0.03] brightness-100 contrast-150 pointer-events-none" />
      </div>

      <div className="mx-auto flex min-h-screen max-w-5xl flex-col px-6 py-8">
        <header className="flex items-center">
          <Link
            to="/"
            className="group flex items-center gap-3 transition-opacity hover:opacity-80"
          >
            <BrandLogo size={34} textColor="currentColor" textSize="1.15rem" />
          </Link>
        </header>

        <div className="flex flex-1 items-center justify-center py-12">
          <Card className="relative w-full max-w-2xl border-border/40 bg-background/60 shadow-[0_32px_64px_-16px_rgba(0,0,0,0.1)] backdrop-blur-2xl dark:bg-card/40">
            <CardHeader className="relative space-y-6 pb-2 pt-12 text-center">
              {/* Animated Icon Container */}
              <div className="relative mx-auto">
                <div
                  className={cn(
                    "absolute inset-0 animate-ping rounded-full opacity-20",
                    status === "success" ? "bg-emerald-500" : "bg-amber-500",
                  )}
                />
                <div
                  className={cn(
                    "relative mx-auto flex h-20 w-20 items-center justify-center rounded-3xl",
                    config.iconWrapClassName,
                  )}
                >
                  <Icon className="size-10" strokeWidth={1.5} />
                </div>
              </div>

              <div className="space-y-2">
                <Badge
                  className={cn(
                    "mx-auto h-6 px-4 uppercase tracking-widest text-[10px]",
                    config.badgeClassName,
                  )}
                >
                  {eyebrow}
                </Badge>
                <CardTitle className="text-4xl font-bold tracking-tight text-foreground">
                  {title}
                </CardTitle>
                <CardDescription className="mx-auto max-w-md text-balance text-base leading-relaxed">
                  {description}
                </CardDescription>
              </div>
            </CardHeader>

            <CardContent className="mt-8 space-y-6 px-10">
              {/* Receipt / Details Box */}
              <div className="group relative overflow-hidden rounded-2xl border border-border/50 bg-muted/30 p-1 transition-all hover:border-border/80">
                <div
                  className={cn(
                    "absolute inset-0 opacity-10",
                    config.panelGlow,
                  )}
                />
                <div className="relative rounded-[14px] bg-background/60 p-4 dark:bg-background/40">
                  <div className="mb-4 flex items-center gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-border/50 bg-background/80 shadow-sm dark:bg-background/60">
                      <Decoration
                        className={cn(
                          "size-5",
                          status === "success"
                            ? "text-emerald-500"
                            : "text-amber-500",
                        )}
                      />
                    </div>
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                        Subscription Summary
                      </p>
                      <p className="text-sm font-medium text-foreground/80">
                        Billing details
                      </p>
                    </div>
                  </div>

                  <div className="overflow-hidden rounded-xl border border-border/50 bg-card/70 dark:bg-card/40">
                    <div className="flex items-center justify-between border-b border-border/50 px-4 py-3">
                      <span className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
                        Product
                      </span>
                      <span className="text-base font-semibold text-foreground">
                        {planName || "Subscription Update"}
                      </span>
                    </div>

                    <div className="flex items-center justify-between border-b border-border/50 px-4 py-3">
                      <span className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
                        Status
                      </span>
                      <span
                        className={cn(
                          "text-base font-bold uppercase",
                          config.statusTextClassName,
                        )}
                      >
                        {status === "success" ? "Active" : "Canceled"}
                      </span>
                    </div>

                    <div className="flex items-center justify-between px-4 py-3">
                      <span className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
                        Billing Cycle
                      </span>
                      <span className="text-base font-semibold text-foreground">
                        Monthly
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <p className="text-center text-sm leading-relaxed text-muted-foreground">
                {detail}
              </p>
            </CardContent>

            <CardFooter className="flex flex-col gap-3 px-10 pb-12 pt-4">
              <Button
                asChild
                size="lg"
                className="h-12 w-full rounded-xl text-base font-semibold shadow-lg transition-transform active:scale-[0.98]"
              >
                <Link
                  to={ctaTo}
                  className="flex items-center justify-center gap-2"
                >
                  {ctaLabel}
                  <ArrowRight className="size-4" />
                </Link>
              </Button>

              {secondaryLabel && secondaryTo && (
                <Button
                  asChild
                  variant="ghost"
                  className="h-12 w-full rounded-xl text-muted-foreground hover:text-foreground"
                >
                  <Link
                    to={secondaryTo}
                    className="flex items-center justify-center gap-2"
                  >
                    <ChevronLeft className="size-4" />
                    {secondaryLabel}
                  </Link>
                </Button>
              )}
            </CardFooter>
          </Card>
        </div>

        <footer className="mt-auto flex flex-col items-center justify-between gap-4 border-t border-border/40 py-6 sm:flex-row">
          <p className="text-xs text-muted-foreground">
            &copy; 2026 Your Brand Inc. All rights reserved.
          </p>
          <div className="flex gap-6">
            <Link
              to="/support"
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              Support
            </Link>
            <Link
              to="/terms"
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              Terms of Service
            </Link>
          </div>
        </footer>
      </div>
    </main>
  );
}
